<template>
  <!-- HEADER -->
  <div class="top-bar">
    <button class="btn-back" @click="goBack">
      <i class="material-icons">arrow_back</i>
    </button>
    <h1>Newsletter</h1>
  </div>

  <div class="newsletter-container">

    <!-- SKELETON -->
    <div v-if="initialLoading" class="card">
      <div class="skeleton skeleton-title"></div>
      <div class="skeleton skeleton-text"></div>
      <div class="skeleton skeleton-text short"></div>
      <div class="skeleton skeleton-input"></div>
      <div class="skeleton skeleton-btn"></div>
    </div>

    <!-- CONTENIDO -->
    <div v-else class="card">

      <h2>📧 Suscríbete</h2>
      <p class="subtitle">
        Recibe las mejores noticias directamente en tu correo.
      </p>

      <ul class="benefits">
        <li>🔥 Contenido exclusivo</li>
        <li>📩 Noticias importantes</li>
        <li>⭐ Sin spam</li>
      </ul>

      <input 
        type="email"
        v-model="email"
        placeholder="Tu correo electrónico"
      />

      <button 
        v-if="!isSubscribed"
        class="btn primary"
        @click="handleSubscribe"
        :disabled="loading"
        >
        {{ loading ? 'Procesando...' : 'Suscribirme' }}
      </button>

      <button 
        v-if="isSubscribed"
        class="btn danger"
        @click="handleUnsubscribe"
      >
        Cancelar suscripción
      </button>

      <p v-if="message" class="message">
        {{ message }}
      </p>

    </div>

  </div>

  <!-- MODAL -->
  <div v-if="showModal" class="modal-overlay">
    <div class="modal">
      <h3>Email ya registrado</h3>
      <p>Este correo ya existe en el sistema.</p>

      <button class="btn primary" @click="reactivate">
        Reactivar suscripción
      </button>

      <button class="btn ghost" @click="resetEmail">
        Usar otro email
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '../../services/api'

const router = useRouter()

const email = ref('')
const loading = ref(false)
const initialLoading = ref(true)
const message = ref('')
const isSubscribed = ref(false)
const showModal = ref(false)

let timeout = null

// CARGA INICIAL
onMounted(async () => {
  const savedEmail = localStorage.getItem('newsletter_email')

  if (savedEmail) {
    email.value = savedEmail
    await checkSubscription()
  }

  setTimeout(() => {
    initialLoading.value = false
  }, 800)
})

// WATCH EMAIL
watch(email, (newEmail) => {
  clearTimeout(timeout)

  timeout = setTimeout(() => {
    if (newEmail && newEmail.includes('@')) {
      checkSubscription()
    }
  }, 500)
})

// CHECK
const checkSubscription = async () => {
  try {
    const res = await api.post('/newsletter/check', {
      email: email.value
    })

    isSubscribed.value = res.data.is_subscribed

  } catch (error) {
    console.error('Error check:', error)
  }
}

// SUBSCRIBE
const handleSubscribe = async () => {
  if (!email.value) {
    message.value = 'Ingresa un correo válido'
    return
  }

  loading.value = true
  message.value = ''

  try {
    const res = await api.post('/newsletter/subscribe', {
      email: email.value
    })

    message.value = res.data.message
    isSubscribed.value = true

    localStorage.setItem('newsletter_email', email.value)

  } catch (error) {

    const err = error.response?.data

    // EMAIL YA EXISTE
    if (err?.errors?.email) {
      showModal.value = true
      return
    }

    message.value = err?.message || 'Error al suscribirse'

  } finally {
    loading.value = false
  }
}

// REACTIVAR
const reactivate = async () => {
  showModal.value = false
  loading.value = true
  message.value = ''

  try {
    const res = await api.post('/newsletter/reactivar', {
      email: email.value
    })

    message.value = res.data.message
    isSubscribed.value = true

  } catch (error) {
    message.value = error.response?.data?.message || 'No se pudo reactivar'
  } finally {
    loading.value = false
  }
}

// LIMPIAR EMAIL
const resetEmail = () => {
  email.value = ''
  showModal.value = false
}

// UNSUBSCRIBE
const handleUnsubscribe = async () => {
  loading.value = true
  message.value = ''

  try {
    const res = await api.post('/newsletter/unsubscribe', {
      email: email.value
    })

    message.value = res.data.message
    isSubscribed.value = false

  } catch (error) {
    message.value = error.response?.data?.message || 'Error'
  } finally {
    loading.value = false
  }
}

// BACK
const goBack = () => {
  if (window.history.length > 1) {
    router.back()
  } else {
    router.push('/mizona')
  }
}
</script>

<style scoped>
.newsletter-container {
  padding: 20px;
}

/* HEADER */
.top-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  position: sticky;
  top: 0;
  background: #fff;
}

.btn-back {
  background: none;
  border: none;
}

.btn-back i {
  font-size: 26px;
  color: #00144a;
}

.top-bar h1 {
  font-size: 18px;
  margin: 0;
  color: #00144a;
  font-weight: 600;
}

/* CARD */
.card {
  background: #fff;
  padding: 20px;
  border-radius: 14px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.06);
}

.benefits {
    list-style: none;
}

/* INPUT */
input {
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #ddd;
  margin-bottom: 10px;
}

/* BOTONES */
.btn {
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  border: none;
  margin-bottom: 8px;
}

.btn.primary {
  background: #1d4ed8;
  color: #fff;
}

.btn.danger {
  background: #dc2626;
  color: #fff;
}

.btn.ghost {
  background: #eee;
}

/* MODAL */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal {
  background: #fff;
  padding: 20px;
  border-radius: 12px;
  width: 90%;
  max-width: 320px;
  text-align: center;
}

/* SKELETON */
.skeleton {
  background: #e5e7eb;
  border-radius: 6px;
  position: relative;
  overflow: hidden;
}

.skeleton::after {
  content: '';
  position: absolute;
  width: 150%;
  height: 100%;
  left: -150%;
  top: 0;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent);
  animation: shimmer 1.2s infinite;
}

@keyframes shimmer {
  100% {
    left: 150%;
  }
}

.skeleton-title {
  height: 20px;
  width: 60%;
  margin-bottom: 10px;
}

.skeleton-text {
  height: 12px;
  margin-bottom: 6px;
}

.skeleton-text.short {
  width: 70%;
}

.skeleton-input {
  height: 40px;
  margin: 10px 0;
}

.skeleton-btn {
  height: 40px;
}
</style>
